import React from "react";
import { ClipboardListIcon, FilePlusIcon, FolderTree, Languages, Settings } from "lucide-react";
import { WorkspaceSummary } from "../../api/v2/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";

export type WorkspaceNavTab = "workspace" | "editor" | "bindings" | "status";

interface WorkspaceSwitcherProps {
  workspaces: WorkspaceSummary[];
  workspaceKey: string;
  activeTab: WorkspaceNavTab;
  isBusy?: boolean;
  onSelectWorkspace: (workspaceKey: string) => void;
  onSelectTab: (tab: WorkspaceNavTab) => void;
}

const NAV_ITEMS: Array<{
  key: WorkspaceNavTab;
  label: string;
  description: string;
  icon?: React.ComponentType<{ className?: string }>;
}> = [
  {
    key: "workspace",
    label: "Workspaces",
    description: "Manage workspaces: create, update, delete, and refresh workspace state.",
    icon: Settings,
  },
  {
    key: "editor",
    label: "Editor",
    description: "Open the main translation editor for localized key-value content.",
    icon: Languages
  },
  {
    key: "bindings",
    label: "Bindings",
    description: "Pick repository files and configure which files are used for translation.",
    icon: FilePlusIcon
  },
  {
    key: "status",
    label: "Status",
    description: "Review workspace logs and operational status.",
    icon: ClipboardListIcon
  },
];

const WorkspaceSwitcher = ({
  workspaces,
  workspaceKey,
  activeTab,
  isBusy = false,
  onSelectWorkspace,
  onSelectTab,
}: WorkspaceSwitcherProps) => {
  return (
    <div className="mb-4 rounded border p-3 flex gap-3 items-center">
      <TooltipProvider>
        <div className="flex flex-wrap items-center gap-2">
          <FolderTree className="h-4 w-4 text-muted-foreground" />
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Select value={workspaceKey} onValueChange={onSelectWorkspace} disabled={isBusy}>
                  <SelectTrigger className="w-[320px]">
                    <SelectValue placeholder="Select workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map((workspace) => (
                      <SelectItem key={workspace.workspaceKey} value={workspace.workspaceKey}>
                        {workspace.workspaceName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm text-xs">
              Select the active workspace where you will configure bindings and edit translations.
            </TooltipContent>
          </Tooltip>
        </div>

        <nav className="flex flex-wrap gap-2" aria-label="Workspace navigation">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.key;
            return (
              <Tooltip key={item.key}>
                <TooltipTrigger asChild>
                  <Button
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    disabled={isBusy}
                    onClick={() => onSelectTab(item.key)}
                    aria-current={isActive ? "page" : undefined}
                    className="gap-1.5"
                  >
                    {Icon ? <Icon className="h-4 w-4" /> : null}
                    {item.label}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm text-xs">{item.description}</TooltipContent>
              </Tooltip>
            );
          })}
        </nav>
      </TooltipProvider>
    </div>
  );
};

export default WorkspaceSwitcher;
